import React, { useMemo, useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Box,
  Button,
  FormControl, 
  IconButton,
  InputAdornment,
  InputLabel, 
  MenuItem, 
  Paper,
  Select, 
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Add, Remove, Search as SearchIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { getMinAndMaxDate, getColumnsUniqueValues, filterProjectRetailerViewData } from './api';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useParams } from "react-router-dom";

import dayjs from 'dayjs';

import { Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import KpiElements from './KpiElement';

Chart.register(...registerables, ChartDataLabels);

const chartOptions = (yAxisTitle, yAxisUnit) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  stacked: false,
  scales: {
    x: {
      ticks: {
        autoSkip: true,
        maxTicksLimit: 10,
      },
    },
    y: {
      title: {
        display: true,
        text: yAxisUnit ? `${yAxisTitle} (${yAxisUnit})` : yAxisTitle,
        font: {
          size: 18,
          weight: 'bold',
        },
      },
    },
  },
  elements: {
    point: {
      radius: 1,
    },
  },
  layout: {
    padding: {
      right: 10,
      top: 10,
      left: 10,
      bottom: 10,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      itemSort: function (a, b) {
        const aValue = a.parsed.y !== null ? a.parsed.y : -Infinity;
        const bValue = b.parsed.y !== null ? b.parsed.y : -Infinity;
        return bValue - aValue;
      },
      callbacks: {
        label: function (context) {
          const label = context.dataset.label || '';
          const value = context.parsed.y !== null ? context.parsed.y.toFixed(2) : 'N/A';
          return yAxisUnit ? `${label}: ${value} ${yAxisUnit}` : `${label}: ${value}`;
        },
      },
    },
    datalabels: {
      align: 'right',
      color: (ctx) => ctx.dataset.borderColor,
      display: (ctx) => ctx.dataIndex === ctx.dataset.data.length - 1,
      formatter: (v, ctx) => ctx.dataset.label,
      offset: 8,
      font: {
        weight: 600,
        size: 10,
      },
    },
  },
});

const InteractiveLegend = React.memo(({ 
  datasets, 
  visibleDatasets, 
  onToggleDataset, 
  onShowAll, 
  onHideAll,
  searchTerm,
  onSearchChange 
}) => {
  const filteredDatasets = useMemo(() => {
    if (!searchTerm) return datasets;
    const term = searchTerm.toLowerCase();
    return datasets.filter(ds => 
      ds.label && ds.label.toLowerCase().includes(term)
    );
  }, [datasets, searchTerm]);

  const allVisible = filteredDatasets.length > 0 && 
    filteredDatasets.every(ds => visibleDatasets.has(ds.label));
  const allHidden = filteredDatasets.length > 0 && 
    filteredDatasets.every(ds => !visibleDatasets.has(ds.label));

  return (
    <Paper 
      elevation={2}
      sx={{ 
        width: 196,
        maxHeight: '420px',
        display: 'flex',
        flexDirection: 'column',
        p: 1.5,
        backgroundColor: '#fafafa',
      }}
    >
      <Typography 
        variant="subtitle2" 
        sx={{ 
          fontWeight: 600, 
          mb: 1.5,
          color: 'text.primary',
          fontSize: '0.7rem'
        }}
      >
        Legend
      </Typography>
      
      <TextField
        size="small"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1.5, '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
      />

      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={onShowAll}
          disabled={allVisible}
          sx={{ flex: 1, fontSize: '0.65rem', py: 0.5 }}
        >
          Show All
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={onHideAll}
          disabled={allHidden}
          sx={{ flex: 1, fontSize: '0.65rem', py: 0.5 }}
        >
          Hide All
        </Button>
      </Box>

      <Box 
        sx={{ 
          overflowY: 'auto',
          flex: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.05)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.3)',
            },
          },
        }}
      >
        {filteredDatasets.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', p: 1 }}>
            No matches found
          </Typography>
        ) : (
          filteredDatasets.map((dataset, idx) => {
            const isVisible = visibleDatasets.has(dataset.label);
            return (
              <Box
                key={dataset.label || idx}
                onClick={() => onToggleDataset(dataset.label)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  px: 1,
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.05)',
                  },
                  opacity: isVisible ? 1 : 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '2px',
                    backgroundColor: dataset.borderColor || dataset.backgroundColor,
                    border: '1px solid rgba(0,0,0,0.1)',
                    flexShrink: 0,
                  }}
                />
                {isVisible ? (
                  <Visibility fontSize="small" sx={{ color: 'text.secondary', fontSize: '0.85rem' }} />
                ) : (
                  <VisibilityOff fontSize="small" sx={{ color: 'text.disabled', fontSize: '0.85rem' }} />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    fontSize: '0.75rem',
                    textDecoration: isVisible ? 'none' : 'line-through',
                    color: isVisible ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {dataset.label}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
    </Paper>
  );
});

function RetailerView() {
  const viewType = 'retailer_view';

  const { projectKey } = useParams();

  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [lineData, setLineData] = useState(null);
  const [gridData, setGridData] = useState(null);
  const [topN, setTopN] = useState(7);
  const [visibleDatasets, setVisibleDatasets] = useState(new Set());
  const [legendSearchTerm, setLegendSearchTerm] = useState('');

  const [columnsUniqueValues, setColumnsUniqueValues] = useState(null);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);

  const [kpiData, setKpiData] = useState(null);

  const handleRetailerChange = (event) => {
    setSelectedRetailer(event.target.value);
    setSelectedProducts([]);
  }

  const handleBrandsChange = (event) => {
    const {
      target: { value },
    } = event;
    let newSelectedBrands = typeof value === 'string' ? value.split(',') : value;
    newSelectedBrands = newSelectedBrands.includes("") ? [] : newSelectedBrands;
    setSelectedBrands(newSelectedBrands);
  }

  const handleProductsChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedProducts(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  }

  const handleStartDateChange = (dateValue) => {
    setStartDate(dateValue);
  }

  const handleEndDateChange = (dateValue) => {
    setEndDate(dateValue);
  }

  // Modify columns to include renderCell for URLs
  const columnsWithClickableUrls = gridData?.columns.map((col) => {

    // TODO: Implement a better filtering than just "url" in column name (value based?)
    if (col.field.toLowerCase().includes('url')) {
      return {
        ...col,
        renderCell: (params) => (
          <a href={params.value} target="_blank" rel="noopener noreferrer">
            {params.value}
          </a>
        ),
      };
    }
    return col;
  });

  useEffect(() => {
    const fetchMinAndMaxDate = async () => {
      // TODO: Duplicated function (repeated in different views) --> deduplicate
      const result = await getMinAndMaxDate(projectKey, viewType);

      if (result) {
        const { min_date: minDateString, max_date: maxDateString } = await getMinAndMaxDate(projectKey, viewType);
        const newMinDate = dayjs(minDateString);
        const newMaxDate = dayjs(maxDateString);
        setMinDate(newMinDate);
        setMaxDate(newMaxDate);

        setStartDate(newMinDate);
        setEndDate(newMaxDate);
      } else {
        console.warn('Failed to fetch min and max dates, leaving default values.');
      }
    };

    const fetchColumnsUniqueValues = async () => {
      const data = await getColumnsUniqueValues(projectKey, viewType);
      if (data) {
        setSelectedRetailer(data.Retailer[0]);
        setSelectedCurrency(data.Currency[0]);
        setColumnsUniqueValues(data);
      } else {
        console.warn('Failed to fetch columns unique values, leaving default values.');
      }
    };

    fetchMinAndMaxDate();
    fetchColumnsUniqueValues();
  }, []); // Empty dependency array to run only once on mount

  useEffect(() => {
    const fetchLineAndGridData = async () => {
      const startDateString = startDate ? startDate.format('YYYY-MM-DD') : null;
      const endDateString = endDate ? endDate.format('YYYY-MM-DD') : null;

      const data = await filterProjectRetailerViewData(
        projectKey,
        viewType,
        selectedRetailer ? [selectedRetailer] : [],
        selectedBrands,
        selectedProducts,
        selectedCurrency,
        startDateString,
        endDateString
      );

      if (data) {
        setLineData(data.graph_data);
        setGridData(data.grid_data);
        setKpiData(data.kpi_data);
        setColumnsUniqueValues({ ...columnsUniqueValues, ...data.columns_unique_values });
      } else {
        console.error('Fetching data failed. No data are being assigned.');
      }
    };

    fetchLineAndGridData();
  }, [projectKey, selectedRetailer, selectedBrands, selectedCurrency, selectedProducts, startDate, endDate]);

  const topNDatasets = useMemo(() => {
    if (!lineData || !lineData.datasets || lineData.datasets.length === 0) {
      return [];
    }
    const datasetsWithAvg = lineData.datasets.map((dataset) => {
      const values = (dataset.data || []).filter((v) => v !== null && v !== undefined && !isNaN(v));
      const avg = values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
      return { dataset, avg };
    });

    datasetsWithAvg.sort((a, b) => b.avg - a.avg);
    return datasetsWithAvg.slice(0, Math.max(1, topN)).map((item) => item.dataset);
  }, [lineData, topN]);

  useEffect(() => {
    if (topNDatasets.length > 0) {
      setVisibleDatasets(new Set(topNDatasets.map((ds) => ds.label)));
    }
  }, [topNDatasets]);

  const chartData = useMemo(() => {
    if (!lineData || !lineData.labels) {
      return null;
    }
    const datasets = visibleDatasets.size
      ? topNDatasets.filter((ds) => visibleDatasets.has(ds.label))
      : topNDatasets;
    return {
      labels: lineData.labels,
      datasets,
    };
  }, [lineData, topNDatasets, visibleDatasets]);

  const handleToggleDataset = (label) => {
    setVisibleDatasets((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const handleShowAll = () => {
    setVisibleDatasets(new Set(topNDatasets.map((ds) => ds.label)));
  };

  const handleHideAll = () => {
    setVisibleDatasets(new Set());
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '100%',
      marginInline: 'auto',
      padding: '0 24px',
      overflowX: 'auto'
    }}>
      <div style={{ minWidth: '1400px' }}>
      <div style={{
        display: 'flex',
        gap: '1rem',
        margin: '1rem 0'
      }}>
        {/* Retailer dropdown */}
        <FormControl fullWidth size="small">
          <InputLabel id="retailer-simple-select-label">Retailer</InputLabel>
          <Select
            labelId="retailer-simple-select-label"
            id="retailer-simple-select"
            value={selectedRetailer}
            label="Retailer"
            onChange={handleRetailerChange}
            MenuProps={{ PaperProps: { style: { maxHeight: '400px' } } }}
          >
            {columnsUniqueValues?.Retailer && columnsUniqueValues?.Retailer.map((retailer, index) => (
              <MenuItem key={index} value={retailer}>{retailer}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Brands dropdown */}
        <Tooltip title={!selectedRetailer ? 'Select Retailer first' : ''}>
          <FormControl fullWidth size="small">
            <InputLabel id="brands-multiple-select-label">Brands</InputLabel>
            <Select
              labelId="brands-multiple-select-label"
              id="brands-multiple-select"
              value={selectedBrands}
              label="Brand"
              multiple
              onChange={handleBrandsChange}
              renderValue={(selected) => selected.join(', ')}
              MenuProps={{ PaperProps: { style: { maxHeight: '400px' } } }}
              disabled={!selectedRetailer}
            >
              <MenuItem value="">-- choose --</MenuItem>
              {columnsUniqueValues?.Brand && columnsUniqueValues?.Brand.map((brand, index) => (
                <MenuItem key={index} value={brand}>{brand}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Tooltip>

        {/* Products dropdown */}
        <Tooltip title={!(selectedBrands && selectedBrands.length) ? 'Select Brand(s) first' : ''} arrow>
          <FormControl fullWidth size="small">
            <InputLabel id="products-multiple-select-label">Product(s)</InputLabel>
            <Select
              labelId="products-multiple-select-label"
              id="products-multiple-select"
              value={selectedProducts}
              label="Product(s)"
              multiple
              onChange={handleProductsChange}
              renderValue={(selected) => selected.join(', ')}
              MenuProps={{ PaperProps: { style: { maxHeight: '400px' } } }}
              disabled={!(selectedBrands && selectedBrands.length)}
            >
              <MenuItem value="">-- choose --</MenuItem>
              {columnsUniqueValues?.Title && columnsUniqueValues?.Title.map((product, index) => (
                <MenuItem key={index} value={product}>{product}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Tooltip>

        {/* Date Pickers */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <FormControl fullWidth size="small">
            <DatePicker slotProps={{ textField: { size: 'small' } }}
              label="Start Date"
              value={startDate}
              onChange={handleStartDateChange}
              disableFuture
              minDate={minDate}
              maxDate={maxDate}
            />
          </FormControl>
          <FormControl fullWidth size="small">
            <DatePicker slotProps={{ textField: { size: 'small' } }}
              label="End Date"
              value={endDate}
              onChange={handleEndDateChange}
              disableFuture
              minDate={minDate}
              maxDate={maxDate}
            />
          </FormControl>
        </LocalizationProvider>

        <FormControl fullWidth size='small'>
          <InputLabel id="currency-simple-select-label">Currency</InputLabel>
          <Select
            labelId="currency-simple-select-label"
            id="currency-simple-select"
            value={selectedCurrency}
            label="Currency"
            onChange={(e) => setSelectedCurrency(e.target.value)}
          >
            {columnsUniqueValues?.Currency.map((currency, index) => (
              <MenuItem key={index} value={currency}>{currency}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div style={{
        display: 'flex',
        gap: '0.5rem',
        margin: '1rem 0'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          flex: '1 1 auto',
          minWidth: 0,
          maxWidth: 'calc(100% - 200px)'
        }}>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Average Price
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                  Show Top:
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setTopN((prev) => Math.max(1, prev - 1))}
                  disabled={topN <= 1}
                >
                  <Remove fontSize="small" />
                </IconButton>
                <TextField
                  value={topN}
                  size="small"
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value) && value >= 1 && value <= 50) {
                      setTopN(value);
                    }
                  }}
                  inputProps={{
                    type: 'number',
                    min: 1,
                    max: 50,
                    style: { textAlign: 'center', width: '50px' }
                  }}
                  sx={{ width: '60px' }}
                />
                <IconButton
                  size="small"
                  onClick={() => setTopN((prev) => Math.min(50, prev + 1))}
                  disabled={topN >= 50}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0, position: 'relative', height: 330, pr: '50px', boxSizing: 'border-box' }}>
                {chartData ? (
                  <Line
                    key="retailer_view_line_chart"
                    options={chartOptions('Average Price', null)}
                    data={chartData}
                  />
                ) : (
                  <Box sx={{ height: 330, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Loading...
                  </Box>
                )}
              </Box>
              {topNDatasets.length > 0 && (
                <InteractiveLegend
                  datasets={topNDatasets}
                  visibleDatasets={visibleDatasets}
                  onToggleDataset={handleToggleDataset}
                  onShowAll={handleShowAll}
                  onHideAll={handleHideAll}
                  searchTerm={legendSearchTerm}
                  onSearchChange={setLegendSearchTerm}
                />
              )}
            </Box>
          </Box>

          <div>
            {gridData ? (
              <DataGrid
                rowHeight={35}
                rows={gridData.rows}
                columns={columnsWithClickableUrls.map(column => ({
                  ...column,
                  resizable: true, // Enable resizable for each column
                  headerAlign: 'center',
                }))}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 100,
                    },
                  },
                }}
                pageSizeOptions={[25, 50, 100]}
                disableSelectionOnClick
                density='compact'
                sx={{
                  height: '350px', // Adjust the height to show ~10 rows (10 * 35 rowHeight)
                  overflowX: 'scroll'
                }}
              />
            ) : (
              <div>Loading...</div>
            )}
          </div>
        </div>

        <div style={{
          width: '160px',
          flexShrink: 0
        }}>
          {KpiElements(kpiData, { size: 'sm' })}
        </div>
      </div>
      </div>
    </div>
  );
}

export default RetailerView;